import { useEffect, useState } from "react";
import { fetchAdminSettings, adminUpdateSettings } from "@/lib/api";
import { HOME_TILES } from "@/lib/images";
import ImageUploader from "@/components/ImageUploader";
import { ImageIcon, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TILES = [
  { key: "hero", label: "Homepage hero background", def: "" },
  { key: "deities", label: "Deities — देवता", def: HOME_TILES.deities },
  { key: "mantras", label: "Mantras — मंत्र", def: HOME_TILES.mantras },
  { key: "stotras", label: "Stotras — स्तोत्र", def: HOME_TILES.stotras },
  { key: "puja-vidhi", label: "Pooja Vidhi — पूजा विधि", def: HOME_TILES["puja-vidhi"] },
  { key: "temples", label: "Temples — मंदिर", def: HOME_TILES.temples },
  { key: "scriptures", label: "Scriptures — शास्त्र", def: HOME_TILES.scriptures },
  { key: "suktas", label: "Suktas — सूक्त", def: HOME_TILES.suktas },
  { key: "festivals", label: "Festivals — पर्व", def: HOME_TILES.festivals },
];

export default function AdminHomepage() {
  const [images, setImages] = useState(null);
  const [featuredCount, setFeaturedCount] = useState(6);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAdminSettings().then((d) => {
      setImages(d.home_images || {});
      setFeaturedCount(d.home_featured_count || 6);
    }).catch(() => setImages({}));
  }, []);

  const setImg = (k, v) => setImages((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = Object.fromEntries(Object.entries(images).filter(([, v]) => v && v.trim()));
      const updated = await adminUpdateSettings({ home_images: cleaned, home_featured_count: Math.max(1, Number(featuredCount) || 6) });
      setImages(updated.home_images || {});
      setFeaturedCount(updated.home_featured_count || 6);
      toast("Homepage settings saved", { description: "Republish (Sync) to update the live site." });
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  if (!images) return <p className="py-20 text-center text-ink-3">…</p>;

  return (
    <div className="mx-auto max-w-4xl" data-testid="admin-homepage">
      <div className="mb-2 flex items-center gap-2.5">
        <ImageIcon className="h-6 w-6 text-saffron" />
        <h1 className="font-serif-latin text-3xl text-ink">Homepage Images</h1>
      </div>
      <p className="mb-8 max-w-2xl text-sm text-ink-3">
        Swap the hero backdrop and the eight section banners shown on the homepage. Leave a slot blank to use the
        designed default. Changes appear on the live site after you run Sync (Preview → Live).
      </p>

      <div className="space-y-6">
        <div className="border border-line bg-surface p-5" data-testid="home-featured-count-card">
          <label className="block text-sm font-medium text-ink">Featured Knowledge — number of cards</label>
          <p className="mt-1 text-xs text-ink-3">
            Mark any article as <strong>Featured</strong> in the Content Library to add it here. This controls how many featured cards appear on the homepage (they lay out in a centred, balanced grid).
          </p>
          <input
            type="number"
            min={1}
            max={24}
            value={featuredCount}
            onChange={(e) => setFeaturedCount(e.target.value)}
            data-testid="home-featured-count"
            className="mt-3 w-28 border border-line bg-[var(--surface)] px-3 py-2 text-ink outline-none focus:border-saffron dh-num"
          />
        </div>
        {TILES.map((tile) => (
          <div key={tile.key} className="border border-line bg-surface p-5" data-testid={`home-img-${tile.key}`}>
            <ImageUploader
              label={tile.label}
              testid={`home-img-${tile.key}`}
              value={images[tile.key] || ""}
              onChange={(v) => setImg(tile.key, v)}
              hint={images[tile.key] ? "" : "Using the designed default — upload or paste a URL to override."}
            />
          </div>
        ))}
      </div>

      <div className="sticky bottom-4 mt-6">
        <button onClick={save} disabled={saving} data-testid="home-img-save" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-7 py-3 text-sm font-medium text-white shadow-lg transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save homepage images
        </button>
      </div>
    </div>
  );
}
